// Shared helpers + small presentational pieces for the strategy report pages
// (Results / Parlay engine / Place engine). All three read GET /api/strategy/report.
import React from 'react';
import type { ReportMetrics } from '../api/strategy';

export const fmtMoney = (n: number | null | undefined) =>
  n == null || !Number.isFinite(Number(n))
    ? '—'
    : `$${Number(n).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export const fmtSignedMoney = (n: number | null | undefined) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  return `${v >= 0 ? '+' : '−'}${fmtMoney(Math.abs(v))}`;
};

export const fmtPct = (n: number | null | undefined, dp = 1) =>
  n == null || !Number.isFinite(Number(n)) ? '—' : `${Number(n).toFixed(dp)}%`;

export const fmtSignedPct = (n: number | null | undefined, dp = 1) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  return `${v >= 0 ? '+' : ''}${v.toFixed(dp)}%`;
};

export const signClass = (n: number | null | undefined) =>
  n == null || !Number.isFinite(Number(n)) ? '' : Number(n) >= 0 ? 'sr-pos' : 'sr-neg';

const FLAG_LABEL: Record<string, string> = { ok: 'reliable', thin: 'indicative', too_few: 'too few' };
const FLAG_TITLE: Record<string, string> = {
  ok: 'n ≥ 100 — read this as signal',
  thin: 'n 30–99 — indicative only, not yet reliable',
  too_few: 'n < 30 — not a signal either way; keep logging',
};

export const Flag: React.FC<{ flag?: string }> = ({ flag = 'too_few' }) => (
  <span className={`sr-flag sr-flag-${flag}`} title={FLAG_TITLE[flag] || FLAG_TITLE.too_few}>
    {FLAG_LABEL[flag] || FLAG_LABEL.too_few}
  </span>
);

// The standard 5-metric tile row for a metrics block. P&L/ROI are LIVE-only; strike/CLV
// use LIVE+PAPER (so they fill faster). CLV is the lead read — it stabilises before ROI.
export const MetricTiles: React.FC<{ m: ReportMetrics; live?: boolean }> = ({ m, live }) => (
  <div className="sr-tiles">
    <div className="sr-tile">
      <span className="sr-tile-label">CLV {live ? '' : '(lead)'}</span>
      <span className={`sr-tile-val ${signClass(m.clv)}`}>{fmtSignedPct(m.clv)}</span>
      <span className="sr-tile-sub">n {m.n} · <Flag flag={m.flag} /></span>
    </div>
    <div className="sr-tile">
      <span className="sr-tile-label">Strike</span>
      <span className="sr-tile-val">{fmtPct(m.strike)}</span>
      <span className="sr-tile-sub">{m.bets} bets</span>
    </div>
    <div className="sr-tile">
      <span className="sr-tile-label">P&amp;L (live)</span>
      <span className={`sr-tile-val ${signClass(m.pnl)}`}>{fmtSignedMoney(m.pnl)}</span>
      <span className="sr-tile-sub">{m.bets_live} placed</span>
    </div>
    <div className="sr-tile">
      <span className="sr-tile-label">ROI (live)</span>
      <span className={`sr-tile-val ${signClass(m.roi)}`}>{fmtSignedPct(m.roi)}</span>
      <span className="sr-tile-sub">noisy at low n</span>
    </div>
    <div className="sr-tile">
      <span className="sr-tile-label">Turnover</span>
      <span className="sr-tile-val">{fmtMoney(m.turnover)}</span>
      <span className="sr-tile-sub">live staked</span>
    </div>
  </div>
);

// A metrics row for a table (engine / day / leg breakdowns).
export const MetricsRow: React.FC<{ label: React.ReactNode; m: ReportMetrics }> = ({ label, m }) => (
  <tr>
    <td className="sr-row-label">{label}</td>
    <td className="sr-num">{m.bets}</td>
    <td className="sr-num">{fmtPct(m.strike)}</td>
    <td className={`sr-num ${signClass(m.clv)}`}>{fmtSignedPct(m.clv)}</td>
    <td className={`sr-num ${signClass(m.roi)}`}>{fmtSignedPct(m.roi)}</td>
    <td className={`sr-num ${signClass(m.pnl)}`}>{fmtSignedMoney(m.pnl)}</td>
    <td className="sr-flag-cell"><Flag flag={m.flag} /></td>
  </tr>
);

export const HonestyNote: React.FC = () => (
  <p className="sr-note">
    P&amp;L, ROI and turnover are <strong>live money only</strong>. Strike rate and CLV use live + paper
    together (so the cells fill faster). <strong>CLV leads</strong> — it stabilises long before ROI, which
    is noise at low sample. Cells flagged <em>too few</em> (n&nbsp;&lt;&nbsp;30) are not a signal either way.
  </p>
);
